import { useQuery } from '@tanstack/react-query';
import { GetBillingQuotaBalanceResponse } from 'network/httpClients/BillingClient/api.schema';
import { BillingClient } from 'network/httpClients/BillingClient/BillingClient';
import { QUERY_KEYS } from 'network/queries/consts';
import {
  QUOTA_FIXTURES,
  getQaBillingState,
} from 'test/fixtures/billing/quotaFixtures';
import { QuotaResponse } from 'types/billing';
import { transformKeysToCamelCase } from 'utils/caseTransformers';

// Query-key discriminators: keep the cache entry for fixture-driven QA state
// separate from the real endpoint so switching states never serves a stale cache.
const QUOTA_SCOPE = 'quota';
const REAL_SOURCE_KEY = 'real';

// Plan name surfaced by the M1 proxy adaptation; replaced by the real plan name
// once GET /quota lands (see TODO(M2) below).
const PROXY_PLAN_NAME = 'Starter';

/**
 * Returns the current /quota as a QuotaResponse.
 *
 * M1 — mock mode: when the URL contains ?qa_billing_state=<state>, the hook
 * returns the corresponding fixture synchronously (React-Query wrapping kept
 * so callers never see a shape change in M2 when the real endpoint replaces it).
 *
 * D7: refetchOnWindowFocus + low staleTime so the banner/page stays fresh on
 * tab-switch and navigation (same pattern as useAzureInstallationsStatusQuery).
 * refetchOnMount: 'always' so re-entering the billing page refetches current
 * state instead of serving the cached result from a previous visit.
 */
export const useQuota = () => {
  const qaState = getQaBillingState();

  return useQuery<QuotaResponse>({
    queryKey: [
      QUERY_KEYS.BILLING_QUOTA_BALANCE,
      QUOTA_SCOPE,
      qaState ?? REAL_SOURCE_KEY,
    ],
    queryFn: async (): Promise<QuotaResponse> => {
      if (qaState !== undefined) {
        return QUOTA_FIXTURES[qaState];
      }

      // TODO(M2, PLT-3713): replace this proxy with GET /quota when the BE
      // endpoint lands. For now, adapt the existing balance endpoint's shape.
      const raw = await BillingClient.getBillingQuotaBalance();
      const balance =
        transformKeysToCamelCase<GetBillingQuotaBalanceResponse>(raw);
      const isOverage = balance.isOverage;
      const overageAmount = balance.overageAmount;
      const credits = balance.credits;

      const adapted: QuotaResponse = {
        trial: null,
        plan: credits
          ? {
              name: PROXY_PLAN_NAME,
              credits_granted: credits.maximum,
              credits_remaining: credits.current,
            }
          : null,
        overage: isOverage
          ? {
              active: true,
              credits: overageAmount,
              cap: null,
              used: overageAmount,
            }
          : null,
        blocked: null,
        cycle: null,
        usage_pct: credits
          ? Math.round(
              ((credits.maximum - credits.current) / credits.maximum) * 100,
            )
          : 0,
      };

      return adapted;
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
};
